import type { ReactNode } from 'react';

import { iterateChartSeriesSlots } from './iterate-config-slots.js';
import { Timeseries } from '../../core/types/timeseries.js';
import { TIMESERIES_CHART_DEFAULT_VARIANT } from '../constants.js';
import {
  TimeseriesVariantSlotProps,
  TimeseriesSlotProps,
  TimeseriesPerVariant,
  ChartVariantInternal,
  ChartVariant,
  type TimeseriesBandSlotProps,
} from '../types/timeseries.js';

/**
 * Checks whether the timeseries have no data.
 * @param timeseries - Given Timeseries[]
 * @returns
 */
export function isEmptyTimeseries(timeseries: TimeseriesSlotProps[]) {
  return (
    timeseries.length === 0 ||
    timeseries.every((series) => series.datapoints.length === 0)
  );
}

/**
 * Function that returns the series splitted per variant.
 */
export function splitTimeseriesPerVariant(
  timeseries: Timeseries[],
  children: ReactNode,
  defaultVariant: ChartVariant,
): TimeseriesPerVariant {
  const seriesFromSlots = iterateChartSeriesSlots(children);

  const timeseriesPerVariant: TimeseriesPerVariant = {
    line: [],
    area: [],
    bar: [],
    band: [],
    shapePriority: [],
  };

  timeseriesPerVariant[defaultVariant ?? TIMESERIES_CHART_DEFAULT_VARIANT] =
    timeseries;

  const shapePriority =
    defaultVariant && seriesFromSlots.shapePriority.includes(defaultVariant)
      ? seriesFromSlots.shapePriority
      : [...seriesFromSlots.shapePriority, defaultVariant];

  return {
    line: timeseriesPerVariant.line.concat(seriesFromSlots.line),
    area: timeseriesPerVariant.area.concat(seriesFromSlots.area),
    bar: timeseriesPerVariant.bar.concat(seriesFromSlots.bar),
    band: timeseriesPerVariant.band.concat(seriesFromSlots.band),
    shapePriority,
  };
}

/**
 * Gets the timestamp of a given datapoint based on the chart variant. Fallback: start timestamp of the datapoint
 * @param datapoint - given TimeseriesDatapoint
 */
export function getDatapointTime(
  start: Date,
  end?: Date,
  center?: Date,
): number {
  if (center) {
    return center.getTime();
  }

  if (end) {
    return start.getTime() + (end.getTime() - start.getTime()) / 2;
  }

  return start.getTime();
}

export const hasSeriesWithDatapoints = (
  dataPerVariant: TimeseriesPerVariant,
): boolean => {
  const data = mergeTimeseriesFromVariants(dataPerVariant);

  return data.length > 0 && data.some((series) => series.datapoints.length);
};

/**
 * Merge all timeseries variant in a single array.
 * Generic types can be used to call it with Timeseries or ColoredTimeseries
 * @param dataPerVariant -
 */
export function mergeTimeseriesFromVariants(
  dataPerVariant: TimeseriesPerVariant,
) {
  const { line, area, bar, band } = dataPerVariant;

  return (
    line as (TimeseriesVariantSlotProps | TimeseriesBandSlotProps)[]
  ).concat(area, bar, band);
}

/**
 * Returns true if there are multiple variants.
 * @param data -
 */
export function hasMultiGeometries<T extends TimeseriesPerVariant>(
  data: T,
): boolean {
  const filteredData = Object.keys(data).map((variant) =>
    variant !== 'shapePriority' ? data[variant as ChartVariantInternal] : [],
  );

  const valuesPerVariant = filteredData.filter(
    (geometryData) => geometryData.length > 0,
  );

  return valuesPerVariant.length > 1;
}
